'use strict';

define('vb/action/builtin/fireCustomEventAction',['vb/action/eventAction', 'vb/action/action', 'vb/private/log', 'vb/private/constants',
  'vb/private/events/eventRegistry',
  'vb/private/events/eventPayloadMetadata',
  'vb/private/action/assignmentHelper',
  'vb/private/utils',
], (EventAction, Action, Log, Constants, EventRegistry, EventPayloadMetadata, AssignmentHelper, Utils) => {
  /** @typedef {import('vb/action/action').Outcome} Outcome */
  /** @typedef {import('vb/private/events/eventModel.js').default} EventModel */

  const logger = Log.getLogger('/vb/private/stateManagement/fireCustomEventAction');
  const {
    cloneEventPayloadWithRowContextMetadataHidden,
    preserveEventPayloadRowContextMetadata,
  } = EventPayloadMetadata;

  /**
   * Clones an event payload after hiding known row-scoped runtime metadata from enumeration.
   *
   * @param {Object} payload event payload
   * @returns {Object} cloned payload
   */
  const cloneEventPayload = (payload) => (payload
    ? cloneEventPayloadWithRowContextMetadataHidden(payload, (source) => Utils.cloneObject(source))
    : payload);

  /**
   * Represents a custom event action that fires a specific event based on the given parameters.
   *
   * @extends EventAction
   * @category Action
   */
  class FireCustomEventAction extends EventAction {
    /**
     * Performs a custom event action based on the given parameters.
     *
     * @param {Object} parameters - The parameters object containing the event details.
     * @param {string} [parameters.name] - The name of the event to fire.
     * @param {string} [parameters.event] - The name of the event to fire. This is used by the Actions api.
     * @param {Object} [parameters.payload] - The payload associated with the event.
     * @param {string} [parameters.target] - The target of the event action.
     * @returns {Promise<Outcome>} A promise resolving to the outcome of the event action
     */
    perform(parameters) {
      const name = parameters.name || parameters.event;
      const { payload } = parameters;

      if (!EventAction.isValid(name)) {
        return Action.createFailureOutcome(`Unable to fire invalid event: ${name}`);
      }

      // there is no good reason this should ever happen
      if (!this.context) {
        return Action
          .createFailureOutcome(`vb/fireCustomEventAction does not have a valid context for event: ${name}.`);
      }

      // look for a declaration; we need to determine if this is a 'normal' VB event,
      // or the dynamic-UI 'dynamicComponent' behavior event.
      const dcEventModel = EventRegistry
        .get(this.context.container, this.context.container, name, Constants.EventBehaviors.DYNAMIC_COMP);

      if (dcEventModel) {
        if (!dcEventModel.isInterface) {
          return Action
            .createFailureOutcome('Events with "template" behavior must be declared in the "interface" section: '
              + `${parameters.target}`);
        }

        return this.performDynamicComponentBehavior(name, payload, dcEventModel);
      }

      if (parameters && !EventAction.isValidTarget(parameters.target)) {
        return Action.createFailureOutcome(`invalid "target" for event action: ${parameters.target}`);
      }

      if (this.eventContainer) {
        // ignore returned Promise; we don't wait for event processing
        // must be declared but need not be part of an interface; interface events are more relevant for extensions
        const eventModel = EventRegistry.get(this.eventContainer, this.eventContainer, name);
        if (eventModel && !this.eventContainer.canFireEvent(eventModel)) {
          return Action.createFailureOutcome(`Unable to find event declaration for: ${name}`);
        }

        const targetContainer = EventAction.getEventTargetContainer(this.eventContainer, name, parameters);
        if (!targetContainer) {
          return Action.createFailureOutcome(`Unable to find target: ${parameters.target}`);
        }

        // to prevent event handlers from making unpredictable changes, pass by value instead of by reference. Event
        // payloads are meant to be serializable any way and must not hold references to state that may not survive
        // the event processing lifecycle. Temporarily hide known row-scoped runtime metadata before cloning so
        // cloneObject does not enumerate lifecycle-sensitive context, then preserve explicit row[prop] access on the
        // cloned payload.
        const clonePayload = cloneEventPayload(payload);
        logger.info('FireCustomEventAction', this.logLabel, 'with event name', name, 'and payload', clonePayload);

        const result = EventAction.fireEventOnTarget(targetContainer, name, clonePayload);
        if (!result) {
          return Action.createFailureOutcome(`Unable to find target: ${parameters.target}`);
        }

        // action waits for promises to resolve
        const promise = Promise.resolve(result);
        return promise
          .then((resolved) => {
            // after current container event listeners -> chains are called, rather than immediately resolve with
            // the result (resolved) perform the propagation behavior before returning resolved. This is a
            // synchronous call when successful
            try {
              EventAction.performPropagationBehavior(
                this.eventContainer, this.internalContext, name, clonePayload, eventModel, {
                  preservePayloadRowContextMetadata: true,
                });
            } catch (e) {
              return Action.createFailureOutcome(e.message, e);
            }

            return Action.createSuccessOutcome(resolved);
          });
      }

      return Action
        .createFailureOutcome(`FireCustomEventAction ${this.logLabel}: unable to fire event ${name}, no page context`);
    }

    /**
     * Fires a custom event with behavior set to 'dynamicComponent'.
     * This has been deprecated! Only to be used by existing applications.
     *
     * @param {string} name - the event name; must be declared in "events", and accessible to this container
     * @param {Object} payload  - event payload
     * @param {EventModel} eventModel - the one for 'behavior:dynamicComponent'
     */
    performDynamicComponentBehavior(name, payload, eventModel) {
      if (!this.eventContainer || !this.eventContainer.allowsDynamicComponentEventBehavior(eventModel.behavior)) {
        const msg = `Unable to fire event: ${name}. Events with 'dynamicComponent' `
          + 'behavior must be fired from a container that supports the behavior, such as Layout';
        logger.error(msg);

        return EventAction.createFailureOutcome(msg);
      }

      // warn users to switch to the 'propagationBehavior:self/container' behavior
      logger.warn('The dynamicComponent behavior is being deprecated in favor of propagationBehavior:container.',
        'Please update event', name, 'to use the propagationBehavior:container behavior.');

      if (!eventModel || !eventModel.isDeclared) {
        return EventAction.createFailureOutcome(`Unable to find event declaration for: ${name}`);
      }

      const clonePayload = cloneEventPayload(payload);
      logger.info('FireCustomEventAction', this.logLabel, 'with event name', name, 'and payload', clonePayload);

      const coercedPayload = eventModel.payloadType
        ? AssignmentHelper.coerceType(clonePayload, eventModel.payloadType) : clonePayload;
      preserveEventPayloadRowContextMetadata(clonePayload, coercedPayload);

      try {
        EventAction.raiseEvent(this.eventContainer, this.internalContext, name, coercedPayload);
      } catch (e) {
        return Action.createFailureOutcome(e.message, e);
      }

      return EventAction.createSuccessOutcome();
    }

    /**
     * Sets the internal context for the FireCustomEventAction instance.
     *
     * @param {Object} context - The external context to be passed to the superclass.
     * @param {*} internalContext - The internal context specific to this instance.
     */
    setInternalContext(context, internalContext) {
      super.setContext(context);
      this.internalContext = internalContext;
    }
  }

  return FireCustomEventAction;
});

